import { camelize, getDateTypeIndex } from '@src/packages/dashboard/utils';

import { EXPRESSION_VALUE_TYPE, GROUP_BY_OPTIONS_VALUES } from '../../../constants';

export const scheduleComplianceHistTransformer = (
  result,
  _filters,
  intl,
  chartGroupBy,
  queryParams,
) => {
  const { data: agg } = camelize(result);
  if (!agg) {
    return {
      data: {
        metrics: [],
        info: {
          totalEvents: 0,
        },
      },
    };
  }

  const results = agg?.aggregations?.histogram;
  const groupQueryParamsBy = queryParams?.groupBy;

  let buckets = results?.buckets || [];
  if (!buckets || !Array.isArray(buckets)) {
    return {
      data: {
        metrics: [],
      },
    };
  }

  let resultData = [];

  switch (groupQueryParamsBy) {
    case GROUP_BY_OPTIONS_VALUES.DIVISIONS:
      resultData = getHistogramResultsByDivisions(
        buckets,
        queryParams?.valueType,
        chartGroupBy,
        intl,
      );
      break;
    case GROUP_BY_OPTIONS_VALUES.GROUPS:
      resultData = getHistogramResultsByGroups(buckets, queryParams?.valueType, chartGroupBy, intl);
      break;
    case GROUP_BY_OPTIONS_VALUES.USERS:
      resultData = getHistogramResultsByUsers(buckets, queryParams?.valueType, chartGroupBy, intl);
      break;
    case GROUP_BY_OPTIONS_VALUES.SCHEDULES:
    case GROUP_BY_OPTIONS_VALUES.TASKS:
    case GROUP_BY_OPTIONS_VALUES.ROUTES:
      resultData = getHistogramResultsBySchedules(
        buckets,
        queryParams?.valueType,
        chartGroupBy,
        intl,
      );
      break;
    case GROUP_BY_OPTIONS_VALUES.TEMPLATES:
      resultData = getHistogramResultsByTemplates(
        buckets,
        queryParams?.valueType,
        chartGroupBy,
        intl,
      );
      break;
    default:
      break;
  }

  return {
    data: {
      metrics: resultData,
    },
  };
};

const getHistogramResultsByDivisions = (buckets, valueType, chartGroupBy, intl) => {
  return buckets?.reduce((acc, bucket) => {
    const dateKey = bucket?.key;
    const results = bucket?.results?.buckets;
    const key = getDateTypeIndex(dateKey, chartGroupBy, intl);

    const rest = results?.reduce?.((acc, result) => {
      const key = result?.division?.hits?.hits?.[0]?.source?.division?.name;
      const value =
        valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE
          ? result?.totalCompletionPercentage?.value
          : result?.triggersNested?.totalFinished?.value;

      return {
        ...acc,
        [key]: Math.round(value || 0),
      };
    }, {});

    acc.push({
      key,
      ...rest,
    });
    return acc;
  }, []);
};

const getHistogramResultsByGroups = (buckets, valueType, chartGroupBy, intl) => {
  return buckets?.reduce((acc, bucket) => {
    const dateKey = bucket?.key;
    const results = bucket?.results?.byGroup?.buckets;
    const key = getDateTypeIndex(dateKey, chartGroupBy, intl);

    const rest = results?.reduce?.((acc, result) => {
      const key = result?.group?.hits?.hits?.[0]?.source?.name;
      const toRoot = result?.toRoot?.triggersMetrics?.value;
      const value =
        valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE
          ? toRoot?.totalCompletionPercentage
          : toRoot?.totalFinished;

      return {
        ...acc,
        [key]: Math.round(value || 0),
      };
    }, {});

    acc.push({
      key,
      ...rest,
    });
    return acc;
  }, []);
};

const getHistogramResultsByUsers = (buckets, valueType, chartGroupBy, intl) => {
  return buckets?.reduce((acc, bucket) => {
    const dateKey = bucket?.key;
    const key = getDateTypeIndex(dateKey, chartGroupBy, intl);

    const results = bucket?.results?.byUser?.buckets;
    const rest = results?.reduce?.((acc, result) => {
      const key = `${result?.user?.hits?.hits?.[0]?.source?.firstName} ${result?.user?.hits?.hits?.[0]?.source?.lastName}`;
      const toRoot = result?.toRoot?.triggersMetrics?.value;
      const value =
        valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE
          ? toRoot?.totalCompletionPercentage
          : toRoot?.totalFinished;

      return {
        ...acc,
        [key]: Math.round(value || 0),
      };
    }, {});

    acc.push({
      key,
      ...rest,
    });
    return acc;
  }, []);
};

const getHistogramResultsBySchedules = (buckets, valueType, chartGroupBy, intl) => {
  return buckets?.reduce((acc, bucket) => {
    const dateKey = bucket?.key;
    const results = bucket?.results?.buckets;
    const key = getDateTypeIndex(dateKey, chartGroupBy, intl);

    const rest = results?.reduce?.((acc, result) => {
      const key = result?.schedule?.hits?.hits?.[0]?.source?.schedule?.name;
      const value =
        valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE
          ? result?.totalCompletionPercentage?.value
          : result?.triggersNested?.totalFinished?.value;

      return {
        ...acc,
        [key]: Math.round(value || 0),
      };
    }, {});

    acc.push({
      key,
      ...rest,
    });
    return acc;
  }, []);
};

const getHistogramResultsByTemplates = (buckets, valueType, chartGroupBy, intl) => {
  return buckets?.reduce((acc, bucket) => {
    const dateKey = bucket?.key;
    const results = bucket?.results?.buckets;
    const key = getDateTypeIndex(dateKey, chartGroupBy, intl);

    const rest = results?.reduce?.((acc, result) => {
      const key = result?.template?.hits?.hits?.[0]?.source?.schedule.taskSummary.name;
      const value =
        valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE
          ? result?.totalCompletionPercentage?.value
          : result?.triggersNested?.totalFinished?.value;

      return {
        ...acc,
        [key]: Math.round(value || 0),
      };
    }, {});

    acc.push({
      key,
      ...rest,
    });
    return acc;
  }, []);
};
